import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Shield } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

/**
 * Consent capture screen for accounts created via Google OAuth (which
 * did not collect consent inline at signup). Rendered when `user.needs_consent`
 * is true. Cannot be dismissed — the coach must accept before using the app.
 */
export default function Consent() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [over18, setOver18] = useState(false);
  const [authority, setAuthority] = useState(false);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);

  const canSubmit = over18 && authority && terms && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/me/consent", {
        over_18_confirmed: over18,
        authority_confirmed: authority,
        terms_accepted: terms,
      });
      await refresh();
      toast.success("Thanks — you're all set.");
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save your consent — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 flex items-center justify-center p-6" data-testid="consent-page">
      <form onSubmit={submit} className="w-full max-w-md bg-white rounded-3xl shadow-xl shadow-blue-500/10 border border-gray-100 p-8">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#007AFF]/10 flex items-center justify-center">
            <Shield className="w-5 h-5 text-[#007AFF]" />
          </div>
          <div>
            <div className="font-display font-bold text-xl tracking-tight">One quick step</div>
            <div className="text-xs text-gray-500">Welcome {user?.name?.split(" ")[0] || "coach"} — before you continue.</div>
          </div>
        </div>

        <p className="mt-6 text-sm text-gray-600 leading-relaxed">
          CoachIQ is for adult coaches and authorized team staff. Please confirm the three items below
          so we know it's okay for you to enter information about the players and teams you coach.
        </p>

        <div className="mt-5 space-y-3 text-[13px] leading-snug text-gray-700">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input data-testid="consent-over18" type="checkbox" checked={over18} onChange={e => setOver18(e.target.checked)} className="mt-0.5 w-4 h-4 rounded accent-[#007AFF]" />
            <span>I confirm I am at least <span className="font-semibold">18 years old</span>.</span>
          </label>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input data-testid="consent-authority" type="checkbox" checked={authority} onChange={e => setAuthority(e.target.checked)} className="mt-0.5 w-4 h-4 rounded accent-[#007AFF]" />
            <span>I confirm I have <span className="font-semibold">authority</span> to enter information about the players and teams I coach, and I will only enter the minimum information needed for coaching.</span>
          </label>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input data-testid="consent-terms" type="checkbox" checked={terms} onChange={e => setTerms(e.target.checked)} className="mt-0.5 w-4 h-4 rounded accent-[#007AFF]" />
            <span>I accept the <Link to="/terms" target="_blank" className="text-[#007AFF] font-semibold hover:underline">Terms</Link> and <Link to="/privacy" target="_blank" className="text-[#007AFF] font-semibold hover:underline">Privacy Policy</Link>.</span>
          </label>
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          data-testid="consent-submit"
          className="mt-6 w-full h-11 rounded-full bg-[#007AFF] hover:bg-[#0056B3] disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold transition-colors shadow-lg shadow-blue-500/20 inline-flex items-center justify-center gap-2"
        >
          <Sparkles className="w-4 h-4" /> {busy ? "Saving…" : "Continue to CoachIQ"}
        </button>

        <button
          type="button"
          onClick={cancel}
          data-testid="consent-decline"
          className="mt-3 w-full h-10 rounded-full text-sm font-semibold text-gray-500 hover:text-gray-800 transition-colors"
        >
          Not now — sign me out
        </button>
      </form>
    </div>
  );
}
